import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import '../components/pulse/pulse.css';
import { PulseHeader } from '../components/pulse/PulseHeader';
import { PulseKpis } from '../components/pulse/PulseKpis';
import { PulseUserActivity } from '../components/pulse/PulseUserActivity';
import { PulseCaseload } from '../components/pulse/PulseCaseload';
import { PulseTasks } from '../components/pulse/PulseTasks';
import { PulseSchedule, PulseHighlights } from '../components/pulse/PulseSchedule';
import { PulseFoodPantry } from '../components/pulse/PulseFoodPantry';
import { VIEWER_TZ } from '../components/pulse/pulseUtils';

const URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/pulse`;
const REFRESH_MS = 30000;
const W = 1920;
const H = 1080;

const useScale = () => {
  const calc = () => Math.min(window.innerWidth / W, window.innerHeight / H);
  const [scale, setScale] = useState(calc);
  useEffect(() => { const on = () => setScale(calc()); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  return scale;
};

const usePulseData = () => {
  const [data, setData] = useState(null);
  const [offline, setOffline] = useState(false);
  const load = useCallback(() => axios.get(URL, { timeout: 20000, params: { tz: VIEWER_TZ } }).then((r) => { setData(r.data); setOffline(false); }).catch(() => setOffline(true)), []);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);
  return { data, offline };
};

export default function PulsePage() {
  const scale = useScale();
  const { data, offline } = usePulseData();
  const [isFull, setIsFull] = useState(Boolean(document.fullscreenElement));
  useEffect(() => { const on = () => setIsFull(Boolean(document.fullscreenElement)); document.addEventListener('fullscreenchange', on); return () => document.removeEventListener('fullscreenchange', on); }, []);
  useEffect(() => { document.title = 'Team Pulse | MyHELP'; }, []);
  const goFull = () => document.documentElement.requestFullscreen?.().catch(() => {});

  return (
    <div className="dark-keep fixed inset-0 bg-white overflow-hidden flex items-center justify-center" data-testid="pulse-page">
      <div className="pulse-root shrink-0" style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: 'center' }}>
        <div className="w-full h-full px-6 pt-3 pb-5 flex flex-col gap-4">
          <PulseHeader offline={offline} onFullscreen={goFull} showFullscreen={!isFull} />
          {!data ? (
            <div className="flex-1 flex items-center justify-center text-[#922A8C]" data-testid="pulse-loading"><Loader2 size={56} className="animate-spin" /></div>
          ) : (
            <>
              <PulseKpis k={data.kpis} />
              <div className="flex-1 min-h-0 grid grid-cols-[520px_minmax(0,1fr)_480px] gap-4">
                <div className="min-h-0"><PulseUserActivity data={data.user_activity} rotateSeconds={data.rotate_seconds} /></div>
                <div className="min-h-0 flex flex-col gap-4">
                  <div className="flex-1 min-h-0"><PulseCaseload data={data.caseload_review} rotateSeconds={data.rotate_seconds} /></div>
                  <div className="h-[232px] shrink-0"><PulseSchedule rows={data.schedule} rotateSeconds={data.rotate_seconds} /></div>
                  <div className="h-[150px] shrink-0"><PulseFoodPantry rows={data.food_pantry || []} /></div>
                </div>
                <div className="min-h-0 flex flex-col gap-4">
                  <div className="flex-1 min-h-0"><PulseTasks t={data.tasks} rotateSeconds={data.rotate_seconds} /></div>
                  <div className="h-[330px] shrink-0"><PulseHighlights h={data.highlights} /></div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
